'use client';
import React, { useEffect, useState, useMemo } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { Search, BookOpen, Mic, FileText, X, Loader2, ScrollText, ThumbsUp, MessageSquare, Flag, PenLine } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import { useAuth } from '@/contexts/AuthContext';
import ReportModal from '@/components/ReportModal';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslations } from '@/lib/i18n';

import { getBulkReactionCounts, type ReactionCounts, type ReactionType, REACTION_META } from '@/lib/db/reactions';

interface PublicContribution {
  id: string;
  title: string;
  excerpt: string | null;
  certainty_label: string;
  era_id: string | null;
  topic_id: string | null;
  region: string | null;
  word_count: number;
  created_at: string;
  author_name: string | null;
  author_id: string | null;
  is_anonymous: boolean;
  upvote_count: number;
  comment_count: number;
  reactions: ReactionCounts;
}

const CERTAINTY_META: Record<string, { label: string; icon: React.ElementType; badge: 'oral' | 'sourced' | 'firsthand' }> = {
  oral_tradition:    { label: 'Oral Tradition',    icon: Mic,      badge: 'oral' },
  sourced_claim:     { label: 'Sourced Claim',     icon: BookOpen, badge: 'sourced' },
  firsthand_account: { label: 'Firsthand Account', icon: FileText, badge: 'firsthand' },
};

const ERA_LABELS: Record<string, string> = {
  'era-1': 'Early Iron Age',
  'era-2': 'Kingdom of Mutapa',
  'era-3': 'Rozvi Empire',
  'era-4': 'Colonial Contact',
  'era-5': 'Anti Colonial Resistance',
  'era-6': 'Nationalist Period',
  'era-7': 'Post-Independence',
};

function ContributionCard({ item, currentUserId }: { item: PublicContribution; currentUserId: string | null }) {
  const meta = CERTAINTY_META[item.certainty_label] || CERTAINTY_META.oral_tradition;
  const d = new Date(item.created_at);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const date = `${String(d.getUTCDate()).padStart(2,'0')} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  const isOwner = !item.is_anonymous && currentUserId && item.author_id === currentUserId;

  return (
    <div
      className="group flex flex-col rounded-xl border overflow-hidden transition-all duration-200 hover:-translate-y-0.5 relative"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--primary)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 24px rgba(224,90,71,0.10)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
      }}
    >
      <Link href={`/contributions/${item.id}`} className="flex flex-col flex-1">
        <div className="h-0.5 w-full" style={{ backgroundColor: 'var(--primary)', opacity: 0.6 }} />
        <div className="flex flex-col flex-1 p-5 gap-3">
          <div className="flex items-start justify-between gap-2">
            <Badge variant={meta.badge} />
            {item.era_id && ERA_LABELS[item.era_id] && (
              <span className="text-xs px-2 py-0.5 rounded-full shrink-0" style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                {ERA_LABELS[item.era_id]}
              </span>
            )}
          </div>
          <h3
            className="text-sm font-semibold leading-snug line-clamp-2"
            style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}
          >
            {item.title}
          </h3>
          {item.excerpt && (
            <p className="text-xs line-clamp-3 flex-1" style={{ color: 'var(--muted-foreground)' }}>
              {item.excerpt}
            </p>
          )}
        </div>
      </Link>
      <div className="px-5 pb-4">
        {/* Reaction counts row */}
        {(item.reactions.insightful > 0 || item.reactions.needs_citation > 0 || item.reactions.eye_opening > 0) && (
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            {(['insightful', 'needs_citation', 'eye_opening'] as ReactionType[]).map(rt => {
              const count = item.reactions[rt];
              if (count === 0) return null;
              const rmeta = REACTION_META[rt];
              return (
                <span
                  key={rt}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border"
                  style={{ backgroundColor: rmeta.bg, borderColor: rmeta.color + '44', color: rmeta.color }}
                >
                  <span>{rmeta.emoji}</span>
                  <span className="font-semibold tabular-nums">{count}</span>
                </span>
              );
            })}
          </div>
        )}
        <div className="flex items-center justify-between pt-1 border-t" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              {item.is_anonymous ? 'Anonymous' : (item.author_name || 'Unknown')}
            </span>
            {item.region && (
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>· {item.region}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {item.upvote_count > 0 && (
              <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                <ThumbsUp size={11} />
                {item.upvote_count}
              </span>
            )}
            {item.comment_count > 0 && (
              <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                <MessageSquare size={11} />
                {item.comment_count}
              </span>
            )}
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{date}</span>
          </div>
        </div>
        {/* Fix 7: Edit entry point for contributors who own this story */}
        {isOwner && (
          <div className="mt-2 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
            <Link
              href={`/contribution-submission?edit=${item.id}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md transition-colors"
              style={{ color: 'var(--primary)', backgroundColor: 'var(--primary)10', border: '1px solid var(--primary)30' }}
            >
              <PenLine size={11} />
              Edit your story
            </Link>
          </div>
        )}
      </div>
      <ReportButtonInCard contentType="contribution" contentId={item.id} contentTitle={item.title} />
    </div>
  );
}

function ReportButtonInCard({ contentType, contentId, contentTitle }: { contentType: 'contribution' | 'forum_post'; contentId: string; contentTitle: string }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className="absolute top-3 right-3 p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-150"
        style={{ backgroundColor: 'var(--muted)', color: 'var(--muted-foreground)' }}
        title="Report this content"
        aria-label="Report"
      >
        <Flag size={12} />
      </button>
      <ReportModal
        open={open}
        onClose={() => setOpen(false)}
        contentType={contentType}
        contentId={contentId}
        contentTitle={contentTitle}
      />
    </>
  );
}

export default function ContributionsListPage() {
  const [contributions, setContributions] = useState<PublicContribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [eraFilter, setEraFilter] = useState('');
  const [certaintyFilter, setCertaintyFilter] = useState('');
  const [reactionFilter, setReactionFilter] = useState<ReactionType | ''>('');
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = getTranslations(language);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('contributions')
      .select('id, title, excerpt, certainty_label, era_id, topic_id, region, word_count, created_at, is_anonymous, author_id')
      .eq('status', 'published')
      .order('created_at', { ascending: false })
      .then(async ({ data, error }) => {
        if (!error && data) {
          const authorIds = [...new Set(data.filter((r: any) => !r.is_anonymous && r.author_id).map((r: any) => r.author_id))];
          let nameMap: Record<string, string> = {};
          if (authorIds.length > 0) {
            const { data: profiles } = await supabase
              .from('user_profiles')
              .select('id, full_name')
              .in('id', authorIds);
            for (const p of profiles || []) {
              nameMap[p.id] = p.full_name || '';
            }
          }

          // Fetch upvote counts
          const contribIds = data.map((r: any) => r.id);
          let upvoteMap: Record<string, number> = {};
          let commentMap: Record<string, number> = {};
          if (contribIds.length > 0) {
            const { data: upvotes } = await supabase
              .from('contribution_upvotes')
              .select('contribution_id')
              .in('contribution_id', contribIds);
            for (const u of upvotes || []) {
              upvoteMap[u.contribution_id] = (upvoteMap[u.contribution_id] || 0) + 1;
            }
            const { data: commentCounts } = await supabase
              .from('contribution_comments')
              .select('contribution_id')
              .in('contribution_id', contribIds);
            for (const c of commentCounts || []) {
              commentMap[c.contribution_id] = (commentMap[c.contribution_id] || 0) + 1;
            }
          }

          // Fetch bulk reaction counts
          const reactionMap = await getBulkReactionCounts('contribution', contribIds);

          setContributions(data.map((row: any) => ({
            id: row.id,
            title: row.title,
            excerpt: row.excerpt,
            certainty_label: row.certainty_label,
            era_id: row.era_id,
            topic_id: row.topic_id,
            region: row.region,
            word_count: row.word_count,
            created_at: row.created_at,
            is_anonymous: row.is_anonymous,
            author_id: row.author_id || null,
            author_name: row.is_anonymous ? null : (nameMap[row.author_id] || null),
            upvote_count: upvoteMap[row.id] || 0,
            comment_count: commentMap[row.id] || 0,
            reactions: reactionMap[row.id] || { insightful: 0, needs_citation: 0, eye_opening: 0 },
          })));
        }
        setLoading(false);
      });
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return contributions.filter((c) => {
      if (eraFilter && c.era_id !== eraFilter) return false;
      if (certaintyFilter && c.certainty_label !== certaintyFilter) return false;
      if (reactionFilter && c.reactions[reactionFilter] === 0) return false;
      if (q && !c.title.toLowerCase().includes(q) && !(c.excerpt || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [contributions, search, eraFilter, certaintyFilter, reactionFilter]);

  const hasFilters = search || eraFilter || certaintyFilter || reactionFilter;
  const clearFilters = () => { setSearch(''); setEraFilter(''); setCertaintyFilter(''); setReactionFilter(''); };

  return (
    <AppLayout>
      <div className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--background)' }}>
        <div className="max-w-screen-xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-2">
              <ScrollText size={18} style={{ color: 'var(--primary)' }} />
              <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
                {t('contributions.communityArchive')}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}>
                  {t('contributions.title')}
                </h1>
                <p className="text-base max-w-2xl" style={{ color: 'var(--muted-foreground)' }}>
                  {t('contributions.subtitle')}
                </p>
              </div>
              <Link
                href="/contribution-submission"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium shrink-0 self-start sm:self-auto transition-all duration-150"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                <PenLine size={15} />
                Add a Story
              </Link>
            </div>
          </div>

          {/* Filters */}
          <div
            className="rounded-xl border px-4 py-3 mb-8 flex flex-wrap items-center gap-3"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
          >
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('contributions.searchPlaceholder')}
                className="w-full pl-8 pr-8 py-1.5 rounded-lg text-xs border bg-transparent outline-none transition-colors"
                style={{ color: 'var(--foreground)', borderColor: search ? 'var(--primary)' : 'var(--border)', backgroundColor: 'var(--muted)' }}
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X size={12} style={{ color: 'var(--muted-foreground)' }} />
                </button>
              )}
            </div>

            <select
              value={eraFilter}
              onChange={(e) => setEraFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-xs border outline-none cursor-pointer"
              style={{ backgroundColor: 'var(--muted)', color: eraFilter ? 'var(--foreground)' : 'var(--muted-foreground)', borderColor: eraFilter ? 'var(--primary)' : 'var(--border)' }}
            >
              <option value="">{t('contributions.allEras')}</option>
              {Object.entries(ERA_LABELS).map(([id, label]) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>

            <select
              value={certaintyFilter}
              onChange={(e) => setCertaintyFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-xs border outline-none cursor-pointer"
              style={{ backgroundColor: 'var(--muted)', color: certaintyFilter ? 'var(--foreground)' : 'var(--muted-foreground)', borderColor: certaintyFilter ? 'var(--primary)' : 'var(--border)' }}
            >
              <option value="">{t('contributions.allTypes')}</option>
              <option value="oral_tradition">{t('certainty.oral_tradition')}</option>
              <option value="sourced_claim">{t('certainty.sourced_claim')}</option>
              <option value="firsthand_account">{t('certainty.firsthand_account')}</option>
            </select>

            {/* Reaction filter */}
            <select
              value={reactionFilter}
              onChange={(e) => setReactionFilter(e.target.value as ReactionType | '')}
              className="px-3 py-1.5 rounded-lg text-xs border outline-none cursor-pointer"
              style={{ backgroundColor: 'var(--muted)', color: reactionFilter ? 'var(--foreground)' : 'var(--muted-foreground)', borderColor: reactionFilter ? 'var(--primary)' : 'var(--border)' }}
            >
              <option value="">{t('contributions.allReactions')}</option>
              <option value="insightful">💡 Insightful</option>
              <option value="needs_citation">📚 Needs Citation</option>
              <option value="eye_opening">👁️ Eye-opening</option>
            </select>

            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs border"
                style={{ color: 'var(--muted-foreground)', borderColor: 'var(--border)' }}
              >
                <X size={11} />{t('contributions.clearFilters')}
              </button>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 size={32} className="animate-spin" style={{ color: 'var(--muted-foreground)' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <ScrollText size={48} className="mb-5 opacity-20" style={{ color: 'var(--muted-foreground)' }} />
              <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--foreground)' }}>
                {hasFilters ? t('contributions.noMatch') : t('contributions.noStories')}
              </h2>
              <p className="text-sm max-w-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
                {hasFilters ? t('contributions.noMatchDesc') : t('contributions.noStoriesDesc')}
              </p>
              {user ? (
                <Link href="/contribution-submission" className="px-5 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                  {t('contributions.submitCta')}
                </Link>
              ) : (
                <Link href="/sign-up-login" className="px-5 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                  {t('contributions.signInCta')}
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((item) => (
                <ContributionCard key={item.id} item={item} currentUserId={user?.id || null} />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
